import React from 'react';

import cx from 'classnames';
import { showModal } from 'modal.react.js';
import { isNil } from 'ramda';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';

import RenderInDomNode from 'components/Pages/Admin/Page/common/RenderInStuff';
import styles from 'components/shared/ActionButtons/index.module.css';
import Button from 'components/shared/Button';
import area from 'components/shared/GlobalModalArea/area';
import Modal from 'components/shared/GlobalModalArea/Modal';
import MapTrifold from 'components/shared/Icon/icons/MapTrifold';
import Share2 from 'components/shared/Icon/icons/Share2';
import Users from 'components/shared/Icon/icons/Users';
import { IconButton } from 'components/shared/IconButton';
import Tooltip, { VARIANTS } from 'components/shared/Tooltip';
import { ACCOUNT_TYPE_AGENT, ITINERARIES_ITEM_TYPES, LIBRARY_ITINERARIES } from 'constants/index';
import { PERMISSION_GUEST_PORTAL, useIsSubscriptionAllowed } from 'hooks/useIsSubscriptionAllowed';
import useMediaBreakpoints from 'hooks/useMediaBreakpoints';
import { useSnackbar } from 'hooks/useSnackbar';
import { useUserActivityChannel } from 'hooks/useUserActivityChannel';
import { itinerariesItemDataSelector } from 'store/itinerariesItem/selectors';
import { appHasChangesSelector } from 'store/unsavedChanges/selectors';
import { accountIdSelector, canCreateItineraryLayoutsSelector, isSuperAdminSelector } from 'store/user/selectors';
import { confirmAlert } from 'utils/alert';
import { useItinerariesItemContext } from 'utils/itineraries/context';
import { isSample } from 'utils/itineraries/functions';
import {
  usePublishSampleItemSaveHandler,
  usePublishSampleLookbookSaveHandler,
} from 'utils/itineraries/functions/builderUtils';
import { useItinerariesItemStepPath } from 'utils/itineraries/functions/useItinerariesItemStepPath';
import { GUEST_PORTAL_STEP_LIST } from 'utils/itineraries/guestPortal/map';
import { ITINERARY_STEPS } from 'utils/itineraries/itinerary/map';
import { ITINERARIES_ITEM_TYPE_TO_LABEL_MAP } from 'utils/itineraries/map';

import { EnterPortalModal } from '../../Itineraries/Table/EnterPortalModal';
import { SaveMenu } from '../../shared/SaveMenu';
import DocxTemplateForm from './DocxTemplateForm';
import s from './index.module.css';

function ItinerariesItemActionButtons({
  hasValidationErrors = false,
  submitting = false,
  onSave,
  onPDF,
  onDOCX,
  onSaveAsSample,
  onPublish = null,
  onIsSavingChange = null,
}) {
  const { libType } = useParams();
  const doesAppHaveChanges = useSelector(appHasChangesSelector);
  const itinerariesItem = useSelector(itinerariesItemDataSelector);
  const isSuperAdmin = useSelector(isSuperAdminSelector);
  const accountId = useSelector(accountIdSelector);
  const { lastStep, isCreating, currentStep, itemType } = useItinerariesItemContext();
  const onSnackbar = useSnackbar();
  const itemTypeLabel = ITINERARIES_ITEM_TYPE_TO_LABEL_MAP[itemType];
  const [isSaving, _setIsSaving] = React.useState(false);
  const isLoading = isSaving || submitting;
  const history = useHistory();
  const getGuestPortalPathStep = useItinerariesItemStepPath(ITINERARIES_ITEM_TYPES.GUEST_PORTAL);
  const getItineraryStepPath = useItinerariesItemStepPath(ITINERARIES_ITEM_TYPES.ITINERARY);
  const onItinerarySamplePublish = usePublishSampleItemSaveHandler();
  const onLookbookSamplePublish = usePublishSampleLookbookSaveHandler();
  const isSampleProposal = isSample(libType);
  const hasSaveAsSampleButton = onSaveAsSample && !isSampleProposal;
  const hasShareButton = !isSampleProposal;
  const hasPublishButton = isSampleProposal && !itinerariesItem.approved;
  const showSaveAsMenu = currentStep === lastStep;
  const isGuestPortalAllowed = useIsSubscriptionAllowed(ACCOUNT_TYPE_AGENT, PERMISSION_GUEST_PORTAL);
  const canCreateItineraryLayouts = useSelector(canCreateItineraryLayoutsSelector);
  const { renderUserBubbles } = useUserActivityChannel(itinerariesItem);
  const { isTabletOrMobile } = useMediaBreakpoints();

  const setIsSaving = (value) => {
    _setIsSaving(value);
    if (onIsSavingChange) onIsSavingChange(value);
  };

  const handlePublish = () => {
    confirmAlert({
      title: 'Do you want to publish this sample now?',
      hintBottom:
        'Publishing will send this sample to Safari Portal for approval and you will not be able to make more changes until the content has been approved.',
      confirmButtonText: 'Confirm',
      onConfirm: async () => {
        try {
          setIsSaving(true);
          if (onPublish) {
            await onPublish(itinerariesItem?.id);
          } else if (itemType === ITINERARIES_ITEM_TYPES.LOOKBOOK) {
            await onLookbookSamplePublish(itinerariesItem?.id);
          } else {
            await onItinerarySamplePublish(itinerariesItem?.id);
          }
        } finally {
          setIsSaving(false);
        }
      },
    });
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      if (isSampleProposal && isSuperAdmin) {
        if (itinerariesItem.approved) {
          confirmAlert({
            title: 'Great Job!',
            hintBottom: 'Your updates are now live.',
            confirmButtonText: 'Save and Publish',
            cancelButtonText: 'Cancel',
            onConfirm: async () => await onSave(),
          });
        } else {
          confirmAlert({
            title: (
              <>
                Your content has been saved as a Draft <br /> in your content library.
              </>
            ),
            hintBottom:
              'If you are ready to make this sample live and share with agents and potential guests, please press Publish.',
            confirmButtonText: 'Publish',
            cancelButtonText: 'Save as Draft',
            onConfirm: async () => {
              await onSave();
              if (onPublish) {
                await onPublish(itinerariesItem?.id);
              } else if (itemType === ITINERARIES_ITEM_TYPES.LOOKBOOK) {
                await onLookbookSamplePublish(itinerariesItem?.id);
              } else {
                await onItinerarySamplePublish(itinerariesItem?.id);
              }
            },
            onCancel: async () => {
              await onSave();
            },
          });
        }
      } else {
        await onSave();
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleShare = () => {
    if (!itinerariesItem) {
      onSnackbar(`There is no confirmed ${itemTypeLabel}`, true);
      return;
    }

    const baseUrl = process.env.NODE_ENV === 'development' ? 'http://localhost:3000' : window.location.origin;
    const proposalType = (() => {
      if (itemType === ITINERARIES_ITEM_TYPES.LOOKBOOK) return 'lookbooks';
      if (itemType === ITINERARIES_ITEM_TYPES.GUEST_PORTAL) return 'guest_portals';

      return 'itineraries';
    })();

    const shareUrl = `${baseUrl}/acc/${accountId}/${proposalType}/${itinerariesItem.id}/share/new?iframe=true`;
    showModal(area, (res) => {
      const handleMessage = (event) => {
        if (event.data === 'closeModal') {
          res();
        }
      };
      window.addEventListener('message', handleMessage);

      const unregister = history.listen(() => {
        res();
        window.removeEventListener('message', handleMessage);
      });

      const handleClose = () => {
        res();
        unregister();
      };

      return (
        <Modal contentClassName={s.shareModal} isOpen toClose={handleClose} withoutCloseBtn>
          <iframe allow="same-origin" src={shareUrl} className={s.shareModalIframe}></iframe>
        </Modal>
      );
    });
  };

  const handleSaveAsDocx = () => {
    showModal(area, (close) => {
      return (
        <DocxTemplateForm
          onSubmit={onDOCX}
          onClose={close}
          proposalType={itinerariesItem.type}
          proposalId={itinerariesItem.id}
          accountId={accountId}
        />
      );
    });
  };

  const goToItinerary = () => {
    confirmAlert({
      title: 'You will be redirected to the Itinerary now.',
      confirmButtonText: "Let's Go!",
      onConfirm: () => {
        history.push(
          getItineraryStepPath(ITINERARY_STEPS.DETAILS, itinerariesItem.itinerary?.id, {
            libType: LIBRARY_ITINERARIES,
          }),
        );
      },
    });
  };

  const goToGuestPortal = () => {
    if (itinerariesItem?.guestPortals.length > 1) {
      return showModal(area, (res) => {
        const unregister = history.listen(() => res(false));
        return (
          <EnterPortalModal
            guestPortals={itinerariesItem.guestPortals}
            res={res}
            onEdit={() => {
              res();
              unregister();
            }}
          />
        );
      });
    }
    confirmAlert({
      title: 'You will be redirected to the Guest Portal now.',
      confirmButtonText: "Let's Go!",
      onConfirm: () => {
        history.push(
          getGuestPortalPathStep(
            itinerariesItem.guestPortalLastAvailableStep ?? GUEST_PORTAL_STEP_LIST[0],
            itinerariesItem.guestPortalId,
            {
              libType: LIBRARY_ITINERARIES,
            },
          ),
        );
      },
    });
  };

  if (isCreating || itinerariesItem.lastAvailableStep !== lastStep) {
    return (
      <RenderInDomNode id="stuff">
        <div className={s.container}>
          <Button
            shape="ellipse"
            type={!doesAppHaveChanges && 'default'}
            size="medium"
            className={styles.button}
            onClick={async () => {
              try {
                setIsSaving(true);
                await onSave();
              } finally {
                setIsSaving(false);
              }
            }}
            disabled={hasValidationErrors || isLoading}
          >
            Save
          </Button>
        </div>
      </RenderInDomNode>
    );
  }

  return (
    <RenderInDomNode id="stuff">
      <div className={s.container}>
        <div className={styles.divided}>
          {renderUserBubbles()}
          {hasShareButton &&
            (isTabletOrMobile ? (
              <Button
                size="medium"
                type="default"
                shape="ellipse"
                icon={Share2}
                className={styles.button}
                onClick={handleShare}
                disabled={
                  hasValidationErrors ||
                  isLoading ||
                  doesAppHaveChanges ||
                  (!itinerariesItem?.policies?.shareWithAgent && !itinerariesItem?.policies?.shareWithClient)
                }
              >
                Share
              </Button>
            ) : (
              <>
                <div data-tip data-for="share-modal-button">
                  <IconButton
                    className={styles.share}
                    icon={Share2}
                    onClick={handleShare}
                    disabled={
                      hasValidationErrors ||
                      isLoading ||
                      doesAppHaveChanges ||
                      (!itinerariesItem?.policies?.shareWithAgent && !itinerariesItem?.policies?.shareWithClient)
                    }
                    iconProps={{ width: '20px', height: '20px' }}
                  />
                  <Tooltip variant={VARIANTS.DARK} id="share-modal-button">
                    Share
                  </Tooltip>
                </div>
              </>
            ))}

          {itemType === ITINERARIES_ITEM_TYPES.GUEST_PORTAL &&
            !isNil(itinerariesItem.itinerary?.id) &&
            (isTabletOrMobile ? (
              <Button
                className={cx(styles.button, styles.headerButton)}
                onClick={goToItinerary}
                size="medium"
                type="default"
                shape="ellipse"
                icon={MapTrifold}
                disabled={isLoading || doesAppHaveChanges}
              >
                Itinerary
              </Button>
            ) : (
              <IconButton
                icon={MapTrifold}
                withTooltip
                tooltipId="go-to-itinerary"
                tooltipContent="View Itinerary"
                className={s.icon}
                onClick={goToItinerary}
                disabled={isLoading || doesAppHaveChanges}
              />
            ))}
          {itemType === ITINERARIES_ITEM_TYPES.ITINERARY &&
            itinerariesItem.confirmed &&
            !isNil(itinerariesItem.guestPortalId) &&
            (isTabletOrMobile ? (
              <Button
                className={cx(styles.button, styles.headerButton)}
                onClick={goToGuestPortal}
                size="medium"
                type="default"
                shape="ellipse"
                icon={Users}
                disabled={isLoading || doesAppHaveChanges || !isGuestPortalAllowed}
              >
                GP
              </Button>
            ) : (
              <IconButton
                icon={Users}
                className={s.icon}
                withTooltip
                tooltipId="go-to-guest-portal"
                tooltipContent="Open Guest Portal"
                onClick={goToGuestPortal}
                disabled={isLoading || doesAppHaveChanges || !isGuestPortalAllowed}
              />
            ))}

          {showSaveAsMenu && (
            <SaveMenu
              buttonClassName={cx(styles.button, styles.saveMenu)}
              type={doesAppHaveChanges ? 'primary' : 'grey'}
              disabled={hasValidationErrors || isLoading}
              isDownloadingInProgress
              itinerariesItemId={itinerariesItem.id}
              canCreateItineraryLayouts={canCreateItineraryLayouts}
              saveCallback={handleSave}
              pdfCallback={onPDF}
              docxCallback={handleSaveAsDocx}
              saveAsSampleCallback={onSaveAsSample}
              hasSaveAsSampleButton={hasSaveAsSampleButton}
            />
          )}

          {!showSaveAsMenu && (
            <>
              <Tooltip disable={!hasValidationErrors} place="bottom" id={'itineraries_save_button'}>
                You have validation errors
              </Tooltip>

              <div data-tip data-for={'itineraries_save_button'} className={styles.buttonContainer}>
                <Button
                  type={!doesAppHaveChanges && 'grey'}
                  shape="ellipse"
                  size="medium"
                  className={styles.button}
                  onClick={handleSave}
                  disabled={hasValidationErrors || isLoading}
                >
                  Save
                </Button>
              </div>
            </>
          )}

          {hasPublishButton && (
            <Button
              size="medium"
              shape="ellipse"
              className={styles.button}
              disabled={hasValidationErrors || isLoading || doesAppHaveChanges}
              onClick={handlePublish}
              type="default"
            >
              Publish
            </Button>
          )}
        </div>
      </div>
    </RenderInDomNode>
  );
}

export default ItinerariesItemActionButtons;
